import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flame, TrendingUp, Sparkles, X, UserCog, BarChart3 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  technology: 'from-sky-500/20 to-sky-500/5 border-sky-500/30',
  tools: 'from-purple-500/20 to-purple-500/5 border-purple-500/30',
  methodology: 'from-amber-500/20 to-amber-500/5 border-amber-500/30',
  domain: 'from-emerald-500/20 to-emerald-500/5 border-emerald-500/30',
  behavioral: 'from-pink-500/20 to-pink-500/5 border-pink-500/30',
  role: 'from-indigo-500/20 to-indigo-500/5 border-indigo-500/30',
  other: 'from-slate-200 dark:from-zinc-800 to-slate-100 dark:to-zinc-900 border-slate-300 dark:border-zinc-700',
};

/**
 * HotTagWidget — Applicant-facing.
 * Shows trending skills/tags this week but does NOT reveal any job postings,
 * titles, or descriptions. Applicants are matched by AI; they should never
 * browse jobs directly (prevents resume tailoring against known openings).
 * CTAs steer the user toward improving their profile OR exploring the skill cloud.
 */
export default function HotTagWidget() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dismissedKey, setDismissedKey] = useState('');

  useEffect(() => {
    const dismissed = sessionStorage.getItem('hot_tag_dismissed_at');
    setDismissedKey(dismissed || '');
    (async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const r = await fetch(`${API_URL}/api/analytics/hot-tags?days=7&limit=4`, { headers: { Authorization: `Bearer ${token}` } });
        const d = await r.json();
        setData(d);
      } catch (e) {
        setError(e.message || 'Failed to load hot tags');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const dismiss = () => {
    const today = new Date().toDateString();
    sessionStorage.setItem('hot_tag_dismissed_at', today);
    setDismissedKey(today);
  };

  const today = new Date().toDateString();
  const isDismissed = dismissedKey === today;

  if (loading) return null;
  if (error) return null;
  if (!data || !data.hot_tags || data.hot_tags.length === 0) return null;
  if (isDismissed) return null;

  const top = data.hot_tags[0];
  const rest = data.hot_tags.slice(1);

  return (
    <div className="mb-8" data-testid="hot-tag-widget">
      <div className={`relative rounded-2xl border p-5 bg-gradient-to-br ${CATEGORY_COLORS[top.category] || CATEGORY_COLORS.other}`}>
        <button onClick={dismiss} aria-label="Dismiss for today" data-testid="hot-tag-dismiss"
          className="absolute top-3 right-3 p-1.5 text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white">
          <X className="w-3.5 h-3.5" />
        </button>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-black/30 flex items-center justify-center shrink-0">
            <Flame className="w-5 h-5 text-orange-600 dark:text-orange-400" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] uppercase tracking-widest text-slate-700 dark:text-white/60">Hot right now</span>
              <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-white/40">· last {data.window_days || 7} days</span>
            </div>
            <HotHeadline tag={top} />
            <p className="text-xs text-slate-600 dark:text-white/60 mt-2 leading-relaxed">
              Recruiters are looking for this skill more than usual. Numuv&apos;s AI matches you automatically &mdash; strengthen your profile to appear in more matches.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={() => navigate('/applicant/profile')}
                data-testid="hot-tag-update-profile-cta"
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-slate-900 dark:bg-white text-white dark:text-black text-xs font-semibold hover:bg-slate-800 dark:hover:bg-zinc-100"
              >
                <UserCog className="w-3.5 h-3.5" />
                Update my profile
              </button>
              <button
                onClick={() => navigate('/applicant/skills')}
                data-testid="hot-tag-explore-skills-cta"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/10 dark:bg-black/30 text-slate-700 dark:text-white/80 hover:bg-black/20 dark:hover:bg-black/50 text-xs"
              >
                <BarChart3 className="w-3.5 h-3.5" />
                Explore skill cloud
              </button>
            </div>
          </div>
        </div>

        {rest.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-200 dark:border-white/10 flex flex-wrap gap-2" data-testid="hot-tag-others">
            <span className="text-[11px] text-slate-600 dark:text-white/50 mr-1 self-center">Also trending:</span>
            {rest.map(t => (
              <button
                key={t.name}
                onClick={() => navigate('/applicant/skills')}
                data-testid={`hot-tag-pill-${t.name}`}
                className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-black/10 dark:bg-black/30 text-slate-700 dark:text-white/80 hover:bg-black/20 dark:hover:bg-black/50 transition-colors"
              >
                <Sparkles className="w-3 h-3 text-amber-700 dark:text-amber-300" />
                {t.name}
                <span className="text-[10px] text-slate-500 dark:text-white/50">{t.is_new ? 'new' : (t.delta_pct > 0 ? `+${t.delta_pct}%` : `${t.delta_pct}%`)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function HotHeadline({ tag }) {
  if (tag.is_new) {
    return (
      <h3 className="text-slate-900 dark:text-white text-lg font-semibold leading-snug" data-testid="hot-tag-headline">
        <span className="text-orange-700 dark:text-orange-300">{tag.name}</span> is rising fast this week.
      </h3>
    );
  }
  return (
    <h3 className="text-slate-900 dark:text-white text-lg font-semibold leading-snug" data-testid="hot-tag-headline">
      <span className="text-orange-700 dark:text-orange-300">{tag.name}</span> demand is up <TrendingUp className="w-4 h-4 inline mb-0.5" /> {tag.delta_pct > 0 ? '+' : ''}{tag.delta_pct}% this week.
    </h3>
  );
}
